import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User, Vehicle } from '../types'

// Auth Store
interface AuthState {
    user: User | null
    isAuthenticated: boolean
    setUser: (user: User | null) => void
    logout: () => void
}

export const useAuthStore = create<AuthState>()(
    persist(
        (set) => ({
            user: null,
            isAuthenticated: false,
            setUser: (user) => set({ user, isAuthenticated: !!user }),
            logout: () => set({ user: null, isAuthenticated: false }),
        }),
        {
            name: 'auth-storage',
        }
    )
)

// Booking Cart Item - For vehicle rentals
export interface BookingCartItem {
    vehicle: Vehicle
    startDate: Date
    endDate: Date
    pickupLocation?: string
    pickupLat?: number
    pickupLng?: number
    dropoffLocation?: string
    deliveryToHotel: boolean
    hotelName?: string
    addons: {
        addonId: number
        included: boolean
    }[]
}

// Cart Store - For booking vehicles
interface CartState {
    items: BookingCartItem[]
    isOpen: boolean
    addItem: (item: BookingCartItem) => void
    removeItem: (vehicleId: number) => void
    updateItem: (vehicleId: number, updates: Partial<BookingCartItem>) => void
    clearCart: () => void
    toggleCart: () => void
    openCart: () => void
    closeCart: () => void
}

export const useCartStore = create<CartState>()(
    persist(
        (set) => ({
            items: [],
            isOpen: false,
            addItem: (item) =>
                set((state) => ({
                    items: [...state.items.filter((i) => i.vehicle.id !== item.vehicle.id), item],
                    isOpen: true,
                })),
            removeItem: (vehicleId) =>
                set((state) => ({
                    items: state.items.filter((i) => i.vehicle.id !== vehicleId),
                })),
            updateItem: (vehicleId, updates) =>
                set((state) => ({
                    items: state.items.map((item) =>
                        item.vehicle.id === vehicleId ? { ...item, ...updates } : item
                    ),
                })),
            clearCart: () => set({ items: [] }),
            toggleCart: () => set((state) => ({ isOpen: !state.isOpen })),
            openCart: () => set({ isOpen: true }),
            closeCart: () => set({ isOpen: false }),
        }),
        {
            name: 'booking-cart-storage',
            partialize: (state) => ({ items: state.items }), // Don't persist isOpen
        }
    )
)

// Language Store - also sets currency automatically
interface LanguageState {
    language: 'en' | 'vi'
    setLanguage: (language: 'en' | 'vi') => void
}

export const useLanguageStore = create<LanguageState>()(
    persist(
        (set) => ({
            language: 'en',
            setLanguage: (language) => {
                set({ language })
                // Auto-set currency based on language
                const currencyStore = useCurrencyStore.getState()
                currencyStore.setCurrency(language === 'en' ? 'USD' : 'VND')
            },
        }),
        {
            name: 'language-storage',
        }
    )
)

// Currency Store
interface CurrencyState {
    currency: 'USD' | 'VND'
    exchangeRate: number // VND per 1 USD
    setCurrency: (currency: 'USD' | 'VND') => void
    setExchangeRate: (rate: number) => void
}

export const useCurrencyStore = create<CurrencyState>()(
    persist(
        (set) => ({
            currency: 'USD',
            exchangeRate: 27000, // Default VND/USD rate - configurable in admin
            setCurrency: (currency) => set({ currency }),
            setExchangeRate: (rate) => set({ exchangeRate: rate }),
        }),
        {
            name: 'currency-storage',
        }
    )
)

// Saved Items Store (Wishlist)
interface SavedItemsState {
    vehicleIds: number[]
    tourIds: number[]
    addVehicle: (id: number) => void
    removeVehicle: (id: number) => void
    addTour: (id: number) => void
    removeTour: (id: number) => void
    isVehicleSaved: (id: number) => boolean
    isTourSaved: (id: number) => boolean
}

export const useSavedItemsStore = create<SavedItemsState>()(
    persist(
        (set, get) => ({
            vehicleIds: [],
            tourIds: [],
            addVehicle: (id) => set((state) => ({ vehicleIds: [...state.vehicleIds, id] })),
            removeVehicle: (id) => set((state) => ({ vehicleIds: state.vehicleIds.filter((v) => v !== id) })),
            addTour: (id) => set((state) => ({ tourIds: [...state.tourIds, id] })),
            removeTour: (id) => set((state) => ({ tourIds: state.tourIds.filter((t) => t !== id) })),
            isVehicleSaved: (id) => get().vehicleIds.includes(id),
            isTourSaved: (id) => get().tourIds.includes(id),
        }),
        {
            name: 'saved-items-storage',
        }
    )
)
